import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, PaginatedResponse, TableQueryParams } from 'types/table.types';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { USER_PATHS } from 'routes/routesConstants';
import { formatRelativeDate } from 'utils/dateFormatter';
import { StaffDto } from 'http-client/types/staff.types';
import {
  useStaffDeactivateMutation,
  useStaffReactivateMutation,
} from 'http-client/hooks/useStaffHooks';
import { usePermissions } from 'context/PermissionProvider';
import { PERMISSIONS } from 'constants/permissions';

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { mutate: deactivateStaff } = useStaffDeactivateMutation();
  const { mutate: reactivateStaff } = useStaffReactivateMutation();

  const columns = useMemo<DataTableColumn<StaffDto>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<StaffDto, unknown>) => (
          <Link
            to={USER_PATHS.EDIT_USER.replace(':id', info.row.original.id)}
            className="text-dark fw-bold"
          >
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email',
        enableSorting: true,
        enableFiltering: true,
      },
      {
        accessorKey: 'role.name',
        id: 'role',
        header: 'Role',
        enableSorting: true,
        enableFiltering: true,
      },
      {
        accessorKey: 'assignedShop.name',
        id: 'shopName',
        header: 'Assigned Shop',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<StaffDto, unknown>) => info.getValue() || '-',
      },
      {
        accessorKey: 'isActive',
        id: 'isActive',
        header: 'Status',
        enableSorting: true,
        enableFiltering: true,
        filterType: 'checkbox',
        cell: (info: CellContext<StaffDto, unknown>) => (
          <span className={`badge ${info.getValue() ? 'bg-success' : 'bg-danger'}`}>
            {info.getValue() ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Created At',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<StaffDto, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<StaffDto, unknown>) => {
          const row = info.row.original;

          const handleToggleActive = () => {
            const action = row.isActive ? 'deactivate' : 'reactivate';
            if (
              window.confirm(
                `${action === 'deactivate' ? 'Deactivate' : 'Reactivate'} employee "${row.name}"?`,
              )
            ) {
              if (row.isActive) {
                deactivateStaff({ id: row.id });
              } else {
                reactivateStaff({ id: row.id });
              }
            }
          };

          return (
            <div className="d-flex gap-2 flex-wrap">
              {hasPermission(PERMISSIONS.EDIT_STAFF) && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate(USER_PATHS.EDIT_USER.replace(':id', row.id))}
                >
                  <i className="ri-edit-line"></i> Edit
                </button>
              )}
              {hasPermission(PERMISSIONS.RESET_PASSWORD) && (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() =>
                    navigate(USER_PATHS.CHANGE_PASSWORD.replace(':id', row.id))
                  }
                  title="Change password"
                >
                  <i className="ri-lock-password-line"></i> Password
                </button>
              )}
              {hasPermission(PERMISSIONS.DELETE_STAFF) && (
                <button
                  className={`btn btn-sm ${row.isActive ? 'btn-danger' : 'btn-success'}`}
                  onClick={handleToggleActive}
                >
                  <i
                    className={row.isActive ? 'ri-user-unfollow-line' : 'ri-user-follow-line'}
                  ></i>
                  {row.isActive ? 'Deactivate' : 'Reactivate'}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, deactivateStaff, reactivateStaff, hasPermission],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<StaffDto>> => {
    return DataTableApiService.fetchPaginatedData<StaffDto>(
      API_ENDPOINTS.TENANT.STAFF.PAGINATED,
      { ...params, includeInactive: true },
    );
  };

  const handleAddUser = () => {
    navigate(USER_PATHS.ADD_USER);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Employees" breadcrumbItem="List" />
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Employees</h5>
              {hasPermission(PERMISSIONS.CREATE_STAFF) && (
                <button className="btn btn-success" onClick={handleAddUser}>
                  <i className="ri-add-line"></i> Add Employee
                </button>
              )}
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.STAFF.PAGINATED}
              onFetchData={handleFetchData}
              enableSearch
              enableFiltering
              enablePagination
              enableSorting
              enableColumnVisibility
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="user_list_visibility"
              columnOrderStorageKey="user_list_order"
              title=""
              emptyMessage="No users found. Add a new employee to get started."
              striped
              hover
              bordered
              responsive
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserListPage;
